import React, { useState, useRef, useEffect } from 'react';
import P from '../texts/p/P';
import Icon from '../icon/Icon';
import CheckBox from '../checkBox/CheckBox';
import styled from 'styled-components';

interface Option {
  label: string;
  value: string;
  badge?: {
    label: string;
    variant?: 'success' | 'neutral';
  };
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  singleSelect?: boolean;
}

const CustomChip = styled.div`
  background-color: #ddf0ed;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  display: flex;
  white-space: nowrap;
  line-height: 1.2;
  min-height: 16px;
  align-items: center;
  justify-content: center;
  color: #01554a;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
`;

const CheckboxGrow = styled.div`
  flex: 1;
  min-width: 0;

  & > div {
    width: 100%;
  }
`;

const StatusBadge = styled.span<{ $variant: 'success' | 'neutral' }>`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.2;
  color: #ffffff;
  background-color: ${({ $variant }) =>
    $variant === 'success' ? '#0C7339' : '#585053'};
`;

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Seleccionar opciones...',
  disabled = false,
  singleSelect = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionToggle = (value: string) => {
    if (disabled) return;

    if (singleSelect) {
      const newSelectedValues = selectedValues.includes(value) ? [] : [value];
      onChange(newSelectedValues);
      setIsOpen(false);
    } else {
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onChange(newSelectedValues);
    }
  };

  const getSelectedLabels = () => {
    return options
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label);
  };

  const selectedLabels = getSelectedLabels();
  const hasSelections = selectedLabels.length > 0;

  const displayValue =
    singleSelect && hasSelections ? selectedLabels[0] : null;

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownTrigger
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        isOpen={isOpen}
      >
        {singleSelect ? (
          displayValue ? (
            <SelectedValue>{displayValue}</SelectedValue>
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )
        ) : hasSelections ? (
          <ChipsContainer>
            {selectedLabels.map((label, index) => (
              <CustomChip key={index}>{label}</CustomChip>
            ))}
          </ChipsContainer>
        ) : (
          <Placeholder>{placeholder}</Placeholder>
        )}
        <ChevronIcon isOpen={isOpen}>
          <Icon
            name='chevron_down_outline'
            variant='neutral'
            width={20}
            height={20}
          />
        </ChevronIcon>
      </DropdownTrigger>

      {isOpen && (
        <DropdownMenu>
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <DropdownOption
                key={option.value}
                isSelected={isSelected}
                onClick={() => !disabled && handleOptionToggle(option.value)}
                clickable={!disabled}
              >
                <OptionRow>
                  <CheckboxGrow>
                    <CheckBox
                      checked={isSelected}
                      onChange={() => handleOptionToggle(option.value)}
                      disabled={disabled}
                      size='sm'
                      customStyle={{
                        backgroundColor: isSelected ? '#028876' : '#ffffff',
                        borderColor: '#008000',
                        checkmarkColor: isSelected ? '#ffffff' : '#028876',
                      }}
                    >
                      <P type='2' variant='neutral' modifier='strong' withMargin={false}>
                        {option.label}
                      </P>
                    </CheckBox>
                  </CheckboxGrow>
                  {option.badge ? (
                    <StatusBadge $variant={option.badge.variant || 'neutral'}>
                      {option.badge.label}
                    </StatusBadge>
                  ) : null}
                </OptionRow>
              </DropdownOption>
            );
          })}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownTrigger = styled.div<{ disabled: boolean; isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 45px;
  padding: 4px 5px;
  border: 1px ${(props) => (props.isOpen ? '#000000' : '#000000')};
  border-radius: 8px;
  background-color: ${(props) => (props.disabled ? '#f5f5f5' : '#ffffff')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  box-shadow: inset 0 0 0 1px #585053;
  position: relative;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 3px;
  align-items: center;
  min-height: 14px;
  padding: 0;
  max-height: 40px;
  overflow-x: auto;
  overflow-y: hidden;
  flex: 1;
`;

const Placeholder = styled.span`
  color: #888;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.1px;
  display: flex;
  align-items: center;
  min-height: 14px;
  padding: 0;
  flex: 1;
`;

const SelectedValue = styled.div`
  background-color: #ddf0ed;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  display: inline-flex;
  white-space: nowrap;
  line-height: 1.2;
  min-height: 16px;
  align-items: center;
  justify-content: center;
  color: #01554a;
`;

const ChevronIcon = styled.div<{ isOpen: boolean }>`
  flex-shrink: 0;
  transition: transform 0.2s ease;
  min-width: 16px;
  width: 20px;
  height: 20px;

  ${(props) =>
    props.isOpen &&
    `
    transform: rotate(180deg);
  `}
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin-top: -1px;
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
`;

const DropdownOption = styled.div<{ isSelected: boolean; clickable: boolean }>`
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background-color 0.15s ease;
  background-color: ${(props) => (props.isSelected ? '#ddf0ed' : '#ffffff')};
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) =>
      props.clickable
        ? props.isSelected
          ? '#d4f0e0'
          : '#f8f9fa'
        : props.isSelected
          ? '#ddf0ed'
          : '#ffffff'};
  }

  &:first-child {
    padding-top: 12px;
  }

  &:last-child {
    padding-bottom: 12px;
  }
`;

export default MultiSelectDropdown;
